import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  checkTppZipExternalChange,
  disconnectTppZipSession,
  downloadCurrentTppZip,
  getTppLocalApiError,
  getTppZipConnection,
  initTppZipAutoSync,
  isTppZipConnected,
  subscribeTppZipConnection,
  teardownTppZipAutoSync,
  type TppZipConnection,
} from '../../../services/transitPassPool.service';
import { invalidateAllTppQueries } from './useTppPageMutations';

type FlashFns = {
  setFlashMessage: (message: string | null) => void;
  setFlashError: (message: string | null) => void;
};

/** Zip 接続状態の購読・外部変更検知・選択リセット・切替・明示DL */
export function useTppZipSession(onSessionReset: () => void, flash: FlashFns) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const flashRef = useRef(flash);
  flashRef.current = flash;
  const [zipConnected, setZipConnected] = useState(() => isTppZipConnected());
  const [zipConn, setZipConn] = useState<TppZipConnection | null>(() => getTppZipConnection());
  const [downloadPending, setDownloadPending] = useState(false);

  useEffect(() => {
    initTppZipAutoSync();
    let prevConnected = isTppZipConnected();
    let prevFileName = getTppZipConnection()?.fileName ?? null;
    let prevDataEpoch = getTppZipConnection()?.dataEpoch ?? 0;
    const syncConnection = () => {
      const next = isTppZipConnected();
      const nextConn = getTppZipConnection();
      const nextFile = nextConn?.fileName ?? null;
      const nextEpoch = nextConn?.dataEpoch ?? 0;
      setZipConnected(next);
      setZipConn(nextConn);
      // dirty / lastSavedAt だけでは invalidate しない（mutation 側の refresh と二重になる）
      // dataEpoch: 同名 Zip 差し替え・外部リロードでも一覧を取り直す
      const sessionChanged =
        !next || !prevConnected || nextFile !== prevFileName || nextEpoch !== prevDataEpoch;
      if (sessionChanged) {
        onSessionReset();
        void invalidateAllTppQueries(qc);
      }
      prevConnected = next;
      prevFileName = nextFile;
      prevDataEpoch = nextEpoch;
    };
    syncConnection();
    const unsub = subscribeTppZipConnection(syncConnection);
    return () => {
      unsub();
      teardownTppZipAutoSync();
    };
  }, [qc, onSessionReset]);

  useEffect(() => {
    if (!zipConnected) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void checkTppZipExternalChange().then((r) => {
          if (r === 'reloaded') {
            void invalidateAllTppQueries(qc);
            flashRef.current.setFlashMessage(
              t('tpp.zip_external_reloaded')
            );
          } else if (r === 'error') {
            flashRef.current.setFlashError(
              t('tpp.zip_external_error')
            );
          }
        });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [zipConnected, qc, t]);

  const handleSwitchZip = useCallback(() => {
    // ピッカー直叩きは失敗しやすい（自動テスト・権限）。ゲートへ戻し新規/既存を選ばせる。
    // 未DL は破棄確認のみ（confirm 後にピッカーを開かないのでジェスチャ問題なし）。
    const conn = getTppZipConnection();
    if (conn?.dirty && !window.confirm(t('tpp.zip_switch_discard_confirm'))) {
      return;
    }
    disconnectTppZipSession();
    onSessionReset();
    void invalidateAllTppQueries(qc);
    flashRef.current.setFlashMessage(t('tpp.zip_switch_to_gate'));
  }, [qc, onSessionReset, t]);

  const handleDownloadZip = useCallback(() => {
    setDownloadPending(true);
    void (async () => {
      try {
        await downloadCurrentTppZip();
        flashRef.current.setFlashMessage(
          t('tpp.zip_download_ok')
        );
      } catch (e: unknown) {
        const msg = getTppLocalApiError(e);
        flashRef.current.setFlashError(
          msg || t('tpp.zip_download_failed')
        );
      } finally {
        setDownloadPending(false);
      }
    })();
  }, [t]);

  return {
    zipConnected,
    zipConn,
    downloadPending,
    handleSwitchZip,
    handleDownloadZip,
  };
}
